import type { ReactNode } from "react";
import clsx from "clsx";

export type BadgeTone = "good" | "warning" | "critical" | "info" | "neutral" | "brand";

// Status tones are reserved and ranked critical > warning > info; neutral is the
// quiet default. Each = border/40 + bg/10 + text, so they read as one family.
const TONE: Record<BadgeTone, string> = {
  good: "border-good/40 bg-good/10 text-good",
  warning: "border-warning/40 bg-warning/10 text-warning",
  critical: "border-critical/40 bg-critical/10 text-critical",
  info: "border-info/40 bg-info/10 text-info",
  brand: "border-brand/40 bg-brand/10 text-brand",
  neutral: "border-hairline bg-surface-3 text-ink-secondary",
};

/**
 * The ONE chip/badge recipe for the whole app (flags, diagnostics, lifecycle,
 * annotation types, status tags). Colour is never the only signal — always pair
 * with a text label (and usually an icon). `title` supplies the hover tooltip.
 */
export function Badge({
  tone = "neutral",
  size = "sm",
  icon,
  title,
  interactive = false,
  className,
  children,
}: {
  tone?: BadgeTone;
  size?: "sm" | "md";
  icon?: ReactNode;
  title?: string;
  /** Adds a help cursor to signal a tooltip. */
  interactive?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={clsx(
        "inline-flex items-center gap-1 rounded-full border font-medium",
        size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs",
        interactive && "cursor-help",
        TONE[tone],
        className
      )}
    >
      {icon}
      {children}
    </span>
  );
}
